import React, { useEffect, useMemo, useState } from "react";
import styles from "./Cadastramento.module.css";
import { usuarioService } from "../../services/(usuario)/UsuarioService";
import { useTemAcessoCadastro } from "../../hooks/useUsuarioLogado";
import UsuarioFormModal from "../../components/UsuarioFormModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import type {
  FiltrosUsuario,
  NivelAcesso,
  StatusUsuario,
  Usuario,
  UsuarioFormValues,
} from "../../types/UsuarioTypes";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";

type AcaoPendente = {
  tipo: "excluir" | "desativar" | "ativar" | "reenviar" | "resetarSenha";
  usuario: Usuario;
} | null;

function statusDoUsuario(usuario: Usuario): StatusUsuario {
  const temExclusao =
    Boolean(usuario.dataExclusao) &&
    usuario.dataExclusao !== "0001-01-01T00:00:00";
  const aguardandoReset = Number(usuario.reset) === 1;

  if (temExclusao) return "Excluído";
  if (usuario.ativo === 1 && usuario.nivel === "0000000000") return "Bloqueado";
  if (usuario.ativo === 0 && (usuario.ativacao === 1 || aguardandoReset))
    return "Aguardando Ativação";
  if (usuario.ativo === 1) return "Ativo";
  if (usuario.ativo === 0) return "Desativado"; // <- novo
  return "";
}

const CORES_STATUS: Record<Exclude<StatusUsuario, "">, string> = {
  Ativo: styles.pillAtivo,
  Bloqueado: styles.pillBloqueado,
  "Aguardando Ativação": styles.pillPendente,
  Desativado: styles.pillDesativado,
  Excluído: styles.pillExcluido,
};

const Cadastramento: React.FC = () => {
  const temAcesso = useTemAcessoCadastro();

  const token = useSelector((state: RootState) => state.auth.token);
  const usuario = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useSessionExpirationCheck(sessionExpiration, token);

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [niveisAcesso, setNiveisAcesso] = useState<NivelAcesso[]>([]);
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [erroLista, setErroLista] = useState<string | null>(null);

  const [filtros, setFiltros] = useState<FiltrosUsuario>({
    order: "CRESCENTE",
    orderBy: "Nome",
  });

  const [usuarioEmEdicao, setUsuarioEmEdicao] = useState<
    Usuario | null | undefined
  >(undefined); // undefined = modal fechado
  const [salvandoUsuario, setSalvandoUsuario] = useState(false);
  const [erroFormulario, setErroFormulario] = useState<string | null>(null);

  const [acaoPendente, setAcaoPendente] = useState<AcaoPendente>(null);
  const [executandoAcao, setExecutandoAcao] = useState(false);

  useEffect(() => {
    if (!temAcesso) return;
    usuarioService
      .niveisAcesso()
      .then(setNiveisAcesso)
      .catch(() => setNiveisAcesso([]));
  }, [temAcesso]);

  useEffect(() => {
    if (!temAcesso) return;
    carregarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temAcesso]);

  function carregarUsuarios(filtrosAtuais: FiltrosUsuario = filtros) {
    setCarregandoLista(true);
    setErroLista(null);
    usuarioService
      .listar(filtrosAtuais)
      .then(setUsuarios)
      .catch((e: Error) => setErroLista(e.message))
      .finally(() => setCarregandoLista(false));
  }

  function handleSubmitFiltros(e: React.FormEvent) {
    e.preventDefault();
    carregarUsuarios(filtros);
  }

  function limparFiltros() {
    const filtrosLimpos: FiltrosUsuario = {
      order: "CRESCENTE",
      orderBy: "Nome",
    };
    setFiltros(filtrosLimpos);
    carregarUsuarios(filtrosLimpos);
  }

  function abrirCriacao() {
    setErroFormulario(null);
    setUsuarioEmEdicao(null);
  }

  function abrirEdicao(usuario: Usuario) {
    setErroFormulario(null);
    setUsuarioEmEdicao(usuario);
  }

  function fecharFormulario() {
    setUsuarioEmEdicao(undefined);
  }

  async function salvarUsuario(valores: UsuarioFormValues) {
    setSalvandoUsuario(true);
    setErroFormulario(null);
    try {
      if (valores.id) {
        await usuarioService.atualizar(valores.id, valores);
      } else {
        await usuarioService.criar(valores);
      }
      fecharFormulario();
      carregarUsuarios();
    } catch (e) {
      setErroFormulario((e as Error).message);
    } finally {
      setSalvandoUsuario(false);
    }
  }

  async function confirmarAcaoPendente() {
    if (!acaoPendente) return;
    setExecutandoAcao(true);
    try {
      const { tipo, usuario } = acaoPendente;
      if (tipo === "excluir") {
        await usuarioService.excluir(usuario.id);
      } else if (tipo === "desativar") {
        await usuarioService.ativarDesativar(usuario.id, false);
      } else if (tipo === "ativar") {
        await usuarioService.ativarDesativar(usuario.id, true);
      } else if (tipo === "reenviar") {
        await usuarioService.reenviarAtivacao(usuario.id, usuario);
      } else if (tipo === "resetarSenha") {
        await usuarioService.resetarSenhaPadrao(usuario.id);
      }
      setAcaoPendente(null);
      carregarUsuarios();
    } catch (e) {
      setErroLista((e as Error).message);
      setAcaoPendente(null);
    } finally {
      setExecutandoAcao(false);
    }
  }

  const textoAcaoPendente = useMemo(() => {
    if (!acaoPendente) return { titulo: "", mensagem: "" };
    const nome = acaoPendente.usuario.nome;
    switch (acaoPendente.tipo) {
      case "excluir":
        return {
          titulo: "Excluir usuário",
          mensagem: `Isso remove o acesso de ${nome} e apaga o e-mail cadastrado. Esta ação não pode ser desfeita pela tela.`,
        };
      case "desativar":
        return {
          titulo: "Desativar usuário",
          mensagem: `${nome} perderá o acesso ao sistema até ser reativado.`,
        };
      case "ativar":
        return {
          titulo: "Ativar usuário",
          mensagem: `${nome} voltará a ter acesso ao sistema.`,
        };
      case "reenviar":
        return {
          titulo: "Reenviar ativação",
          mensagem: `Um novo e-mail de ativação será enviado para ${nome}.`,
        };
      case "resetarSenha":
        return {
          titulo: "Resetar senha padrão",
          mensagem: `A senha de ${nome} será redefinida para a senha padrão de ativação e o acesso será liberado imediatamente, sem precisar do e-mail de ativação.`,
        };
      default:
        return { titulo: "", mensagem: "" };
    }
  }, [acaoPendente]);

  if (!temAcesso) {
    return (
      <div className={styles.pagina}>
        <div className={styles.acessoNegado}>
          <span className={styles.acessoNegadoCodigo}>403</span>
          <h1>Acesso restrito</h1>
          <p>Esta página é exclusiva para usuários com nível de acesso 2.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pagina}>
      <header className={styles.cabecalhoPagina}>
        <div>
          <p className={styles.eyebrow}>Administração · Usuários</p>
          <h1>Cadastramento de usuários</h1>
        </div>
        <button
          type="button"
          className={styles.botaoNovo}
          onClick={abrirCriacao}
        >
          + Novo usuário
        </button>
      </header>

      <form className={styles.filtros} onSubmit={handleSubmitFiltros}>
        <input
          type="text"
          placeholder="Nome"
          value={filtros.nome ?? ""}
          onChange={(e) => setFiltros((f) => ({ ...f, nome: e.target.value }))}
        />
        <input
          type="text"
          placeholder="Login"
          value={filtros.login ?? ""}
          onChange={(e) => setFiltros((f) => ({ ...f, login: e.target.value }))}
        />
        <input
          type="text"
          placeholder="E-mail"
          value={filtros.email ?? ""}
          onChange={(e) => setFiltros((f) => ({ ...f, email: e.target.value }))}
        />
        <select
          value={filtros.status ?? ""}
          onChange={(e) =>
            setFiltros((f) => ({
              ...f,
              status: e.target.value as StatusUsuario,
            }))
          }
        >
          <option value="">Todos os status</option>
          <option value="Ativo">Ativo</option>
          <option value="Bloqueado">Bloqueado</option>
          <option value="Aguardando Ativação">Aguardando ativação</option>
          <option value="Excluído">Excluído</option>
        </select>
        <div className={styles.filtrosAcoes}>
          <button type="submit" className={styles.botaoFiltrar}>
            Filtrar
          </button>
          <button
            type="button"
            className={styles.botaoLimpar}
            onClick={limparFiltros}
          >
            Limpar
          </button>
        </div>
      </form>

      {erroLista && <div className={styles.faixaErro}>{erroLista}</div>}

      {carregandoLista ? (
        <div className={styles.estadoVazio}>Carregando usuários…</div>
      ) : usuarios.length === 0 ? (
        <div className={styles.estadoVazio}>
          <p>Nenhum usuário encontrado para os filtros atuais.</p>
        </div>
      ) : (
        <>
          <table className={styles.tabela}>
            <thead>
              <tr>
                <th className={styles.colMono}>ID</th>
                <th>Nome</th>
                <th>Login</th>
                <th>E-mail</th>
                <th>Status</th>
                <th className={styles.colAcoes}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((usuario) => {
                const status = statusDoUsuario(usuario);
                return (
                  <tr key={usuario.id}>
                    <td className={styles.colMono}>
                      #{String(usuario.id).padStart(4, "0")}
                    </td>
                    <td>{usuario.nome}</td>
                    <td>{usuario.login}</td>
                    <td>{usuario.email}</td>
                    <td>
                      {status && (
                        <span
                          className={`${styles.pill} ${CORES_STATUS[status]}`}
                        >
                          {status}
                        </span>
                      )}
                    </td>
                    <td className={styles.colAcoes}>
                      <div className={styles.grupoAcoes}>
                        <button
                          type="button"
                          onClick={() => abrirEdicao(usuario)}
                          title="Editar"
                        >
                          Editar
                        </button>
                        {status === "Ativo" ? (
                          <button
                            type="button"
                            className={styles.acaoAlerta}
                            onClick={() =>
                              setAcaoPendente({ tipo: "desativar", usuario })
                            }
                            title="Desativar"
                          >
                            Desativar
                          </button>
                        ) : status === "Bloqueado" ||
                          status === "Desativado" ? (
                          <button
                            type="button"
                            className={styles.acaoPositiva}
                            onClick={() =>
                              setAcaoPendente({ tipo: "ativar", usuario })
                            }
                            title="Ativar"
                          >
                            Ativar
                          </button>
                        ) : status === "Aguardando Ativação" ? (
                          <button
                            type="button"
                            onClick={() =>
                              setAcaoPendente({ tipo: "reenviar", usuario })
                            }
                            title="Reenviar convite de ativação"
                          >
                            Reenviar
                          </button>
                        ) : null}
                        {/* ⬇️ bloco que faltava */}
                        {status !== "Excluído" && (
                          <button
                            type="button"
                            className={styles.acaoPerigo}
                            onClick={() =>
                              setAcaoPendente({ tipo: "excluir", usuario })
                            }
                            title="Excluir"
                          >
                            Excluir
                          </button>
                        )}
                        {status !== "Excluído" && (
                          <button
                            type="button"
                            className={styles.acaoInfo}
                            onClick={() =>
                              setAcaoPendente({ tipo: "resetarSenha", usuario })
                            }
                            title="Resetar senha padrão"
                          >
                            Resetar senha
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className={styles.listaCards}>
            {usuarios.map((usuario) => {
              const status = statusDoUsuario(usuario);
              return (
                <div key={usuario.id} className={styles.card}>
                  <div className={styles.cardCabecalho}>
                    <div>
                      <span className={styles.cardId}>
                        #{String(usuario.id).padStart(4, "0")}
                      </span>
                      <h3>{usuario.nome}</h3>
                    </div>
                    {status && (
                      <span
                        className={`${styles.pill} ${CORES_STATUS[status]}`}
                      >
                        {status}
                      </span>
                    )}
                  </div>
                  <dl className={styles.cardDados}>
                    <div>
                      <dt>Login</dt>
                      <dd>{usuario.login}</dd>
                    </div>
                    <div>
                      <dt>E-mail</dt>
                      <dd>{usuario.email}</dd>
                    </div>
                  </dl>
                  <div className={styles.grupoAcoes}>
                    <button type="button" onClick={() => abrirEdicao(usuario)}>
                      Editar
                    </button>
                    {status === "Ativo" ? (
                      <button
                        type="button"
                        className={styles.acaoAlerta}
                        onClick={() =>
                          setAcaoPendente({ tipo: "desativar", usuario })
                        }
                      >
                        Desativar
                      </button>
                    ) : status === "Bloqueado" ? (
                      <button
                        type="button"
                        className={styles.acaoPositiva}
                        onClick={() =>
                          setAcaoPendente({ tipo: "ativar", usuario })
                        }
                      >
                        Ativar
                      </button>
                    ) : status === "Aguardando Ativação" ? (
                      <button
                        type="button"
                        onClick={() =>
                          setAcaoPendente({ tipo: "reenviar", usuario })
                        }
                      >
                        Reenviar
                      </button>
                    ) : null}
                    {status !== "Excluído" && (
                      <button
                        type="button"
                        className={styles.acaoPerigo}
                        onClick={() =>
                          setAcaoPendente({ tipo: "excluir", usuario })
                        }
                      >
                        Excluir
                      </button>
                    )}
                    {status !== "Excluído" && (
                      <button
                        type="button"
                        className={styles.acaoInfo}
                        onClick={() =>
                          setAcaoPendente({ tipo: "resetarSenha", usuario })
                        }
                        title="Resetar senha padrão"
                      >
                        Resetar senha
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {usuarioEmEdicao !== undefined && (
        <UsuarioFormModal
          usuario={usuarioEmEdicao}
          niveisAcesso={niveisAcesso}
          carregando={salvandoUsuario}
          erro={erroFormulario}
          onSalvar={salvarUsuario}
          onFechar={fecharFormulario}
        />
      )}

      {acaoPendente && (
        <ConfirmDialog
          titulo={textoAcaoPendente.titulo}
          mensagem={textoAcaoPendente.mensagem}
          variante={
            acaoPendente.tipo === "excluir" || acaoPendente.tipo === "desativar"
              ? "perigo"
              : "padrao"
          }
          rotuloConfirmar={
            acaoPendente.tipo === "excluir" ? "Excluir" : "Confirmar"
          }
          carregando={executandoAcao}
          onConfirmar={confirmarAcaoPendente}
          onCancelar={() => setAcaoPendente(null)}
        />
      )}
    </div>
  );
};

export default Cadastramento;
